/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2025
///                                   RXO Inc.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of RXO Inc. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import { UserManager, WebStorageStateStore, Log } from "oidc-client";

const webClientUrl = window.REACT_APP_RFQ_WEB_CLIENT_URL
  ? window.REACT_APP_RFQ_WEB_CLIENT_URL.slice(0, -1)
  : "";
const rxoIdentityServerUrl = window.REACT_APP_RXO_IDENTITY_SERVER_URL;
const rxoIdentityServerIssuerUrl =
  window.REACT_APP_RXO_IDENTITY_SERVER_ISSUER_URL;
const rxoClientIdentifier = window.REACT_APP_RXO_CLIENT_IDENTIFIER
  ? window.REACT_APP_RXO_CLIENT_IDENTIFIER
  : process.env.REACT_APP_RXO_CLIENT_IDENTIFIER;
const rxoClientSecret = window.REACT_APP_RXO_CLIENT_SECRET
  ? window.REACT_APP_RXO_CLIENT_SECRET
  : process.env.REACT_APP_RXO_CLIENT_SECRET;
const oidcStorageUser = `oidc.user:${rxoIdentityServerUrl}:${rxoClientIdentifier}`;

const rxoAuthConfig = {
  authority: rxoIdentityServerUrl,
  client_id: rxoClientIdentifier,
  redirect_uri: `${webClientUrl}/signin-oidc`,
  response_type: "code",
  scope: "openid offline_access",
  post_logout_redirect_uri: webClientUrl,
  nonce: webClientUrl,
  acr_values: "idp:oidc-rxo-azure",
  client_secret: rxoClientSecret,
  automaticSilentRenew: true,
  silent_redirect_uri: `${webClientUrl}/silentrenew`
};

const METADATA_OIDC_RXO = {
  issuer: rxoIdentityServerIssuerUrl.toLowerCase(),
  jwks_uri:
    window.REACT_APP_RXO_IDENTITY_SERVER_URL +
    "/.well-known/openid-configuration/jwks",
  authorization_endpoint:
    window.REACT_APP_RXO_IDENTITY_SERVER_URL + "/connect/authorize",
  token_endpoint: window.REACT_APP_RXO_IDENTITY_SERVER_URL + "/connect/token",
  userinfo_endpoint:
    window.REACT_APP_RXO_IDENTITY_SERVER_URL + "/connect/userinfo",
  end_session_endpoint:
    window.REACT_APP_RXO_IDENTITY_SERVER_URL + "/connect/endsession",
  revocation_endpoint:
    window.REACT_APP_RXO_IDENTITY_SERVER_URL + "/connect/revocation",
  introspection_endpoint:
    window.REACT_APP_RXO_IDENTITY_SERVER_URL + "/connect/introspect"
};
class AuthService {
  UserManager;
  constructor() {
    this.UserManager = new UserManager({
      ...rxoAuthConfig,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      metadata: {
        ...METADATA_OIDC_RXO
      }
    });

    // Logger
    Log.logger = console;
    Log.level = Log.ERROR;
    this.UserManager.events.addUserLoaded(user => {
      if (window.location.href.indexOf("signin-oidc") !== -1) {
        this.navigateToScreen();
      }
    });
    this.UserManager.events.addSilentRenewError(e => {
      console.debug("silent renew error", e.message);
    });
  }

  signinRedirectCallback = () => {
    this.UserManager.signinRedirectCallback().then(() => {
      "";
    });
  };

  getUser = async () => {
    const user = await this.UserManager.getUser();
    if (!user) {
      return await this.UserManager.signinRedirectCallback();
    }
    return user;
  };

  parseJwt = token => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace("-", "+").replace("_", "/");
    return JSON.parse(window.atob(base64));
  };

  signinRedirect = () => {
    localStorage.setItem(
      "redirectUri",
      window.location.pathname + window.location.search
    );
    this.UserManager.signinRedirect({});
  };

  navigateToScreen = () => {
    const redirectUri = localStorage.getItem("redirectUri");
    window.location.replace(
      redirectUri ? webClientUrl + redirectUri : webClientUrl
    );
  };

  isAuthenticated = () => {
    const oidcStorage = JSON.parse(sessionStorage.getItem(oidcStorageUser));

    return !!oidcStorage && !!oidcStorage.access_token;
  };

  signinSilent = () => {
    this.UserManager.signinSilent()
      .then(user => {
        console.debug("signed in", user);
      })
      .catch(err => {
        console.error(err);
      });
  };
  signinSilentCallback = () => {
    this.UserManager.signinSilentCallback();
  };

  createSigninRequest = () => {
    return this.UserManager.createSigninRequest();
  };

  logout = () => {
    this.UserManager.signoutRedirect({
      id_token_hint: localStorage.getItem("id_token")
    });
    this.UserManager.clearStaleState();
  };

  signoutRedirectCallback = () => {
    this.UserManager.signoutRedirectCallback().then(() => {
      localStorage.clear();
      window.location.replace(process.env.REACT_APP_PUBLIC_URL);
    });
    this.UserManager.clearStaleState();
  };
}

const getAccessToken = () => {
  const oidcStorage = JSON.parse(sessionStorage.getItem(oidcStorageUser));
  return oidcStorage?.access_token ?? null;
};

export { AuthService, getAccessToken };
